import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, TextField, Button, Grid, Card, CardContent, Alert } from '@mui/material';
import Spinner from '../../components/Spinner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

const EtymologyIndex: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let queryStr = `?type=etymology`;
      if (search.trim()) {
        queryStr += `&search=${encodeURIComponent(search.trim())}`;
      }

      const res = await api.get(`/language-hub${queryStr}`);
      if (res.data && res.data.success) {
        setEntries(res.data.data);
      } else {
        setError('Failed to fetch etymology entries.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error connecting to etymology API');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchEntries();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back button */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/language-hub')} 
        sx={{ mb: 4 }}
      >
        Language Hub
      </Button>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
          Etymology Index
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Deep dive into word origins. Uncover the rich historical development, roots, and linguistic maps behind common English terms.
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 5 }}>
        <form onSubmit={handleSearchSubmit}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search word origins (e.g. Phobia, Chronology)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
            />
            <Button type="submit" variant="contained" size="large" sx={{ px: 4 }}>
              Search
            </Button>
          </Box>
        </form>
      </Box>

      {/* Error display */}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {/* Etymology index */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Spinner />
        </Box>
      ) : entries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">No etymology entries found matching your query.</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {entries.map((item) => (
            <Grid item xs={12} md={6} key={item._id}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 4, 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.04) 0%, rgba(6, 182, 212, 0.01) 100%)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(6, 182, 212, 0.02) 100%)',
                    borderColor: '#06b6d4'
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 800, color: '#22d3ee' }}>
                      {item.word}
                    </Typography>
                    <Button variant="outlined" color="info" size="small" sx={{ borderRadius: 4, pointerEvents: 'none' }}>
                      Root: {item.origin || 'Unknown'}
                    </Button>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                    Definition: <Typography component="span" variant="body1" color="text.secondary">{item.definition}</Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, backgroundColor: 'rgba(255,255,255,0.01)', p: 2, borderRadius: 2, borderLeft: '3px solid #06b6d4' }}>
                    <strong>Origin & History:</strong> {item.history}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default EtymologyIndex;
